import { createContext, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { pathFor } from "@/navConfig";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/i18n/LanguageContext";

const FormDialogsContext = createContext(null);
export const useFormDialogs = () => useContext(FormDialogsContext);

const inputClass =
  "bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#CEA26B] rounded-none";
const submitClass =
  "w-full bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] py-3 font-semibold tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

const postForm = async (path, data) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("submit failed");
};

const TicketDialog = ({ open, onOpenChange, prefill }) => {
  const { t, lang } = useLanguage();
  const d = t.ticketDialog;
  const [type, setType] = useState(prefill || "");
  const [sending, setSending] = useState(false);
  const { register, handleSubmit, reset } = useForm();

  const onSubmit = async (data) => {
    setSending(true);
    try {
      await postForm("register", { ...data, ticketType: type });
      toast.success(d.success);
      reset();
      setType("");
      onOpenChange(false);
    } catch {
      toast.error(t.common.submitError);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A2060] border-white/10 text-white sm:max-w-lg" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{d.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{d.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300">{d.name}</Label>
            <Input required className={inputClass} data-testid="ticket-name-input" {...register("name")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">{d.email}</Label>
              <Input required type="email" className={inputClass} data-testid="ticket-email-input" {...register("email")} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">{d.company}</Label>
              <Input className={inputClass} data-testid="ticket-company-input" {...register("company")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">{d.ticketType}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className={inputClass} data-testid="ticket-type-select">
                <SelectValue placeholder={t.common.selectPlaceholder} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A2060] border-white/10 text-white">
                {t.tickets.types.map((it, i) => (
                  <SelectItem key={i} value={it.name} className="focus:bg-white/10 focus:text-white">
                    {it.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">{d.message}</Label>
            <Textarea className={inputClass} rows={3} data-testid="ticket-message-input" {...register("message")} />
          </div>
          <button type="submit" disabled={sending} className={submitClass} data-testid="ticket-submit-btn">
            {sending ? "…" : d.submit}
          </button>
          <p className="text-xs text-slate-500 leading-relaxed" data-testid="ticket-gdpr">
            {t.common.gdprNotice}{" "}
            <Link to={pathFor("privacy", lang)} onClick={() => onOpenChange(false)} className="text-slate-400 underline underline-offset-2 hover:text-[#CEA26B] transition-colors">
              {t.privacy.title}
            </Link>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PartnerDialog = ({ open, onOpenChange }) => {
  const { t, lang } = useLanguage();
  const d = t.partnerDialog;
  const [sending, setSending] = useState(false);
  const { register, handleSubmit, reset } = useForm();

  const onSubmit = async (data) => {
    setSending(true);
    try {
      await postForm("partner", data);
      toast.success(d.success);
      reset();
      onOpenChange(false);
    } catch {
      toast.error(t.common.submitError);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A2060] border-white/10 text-white sm:max-w-lg" data-testid="partner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{d.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{d.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300">{d.name}</Label>
            <Input required className={inputClass} data-testid="partner-name-input" {...register("name")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">{d.email}</Label>
              <Input required type="email" className={inputClass} data-testid="partner-email-input" {...register("email")} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">{d.company}</Label>
              <Input required className={inputClass} data-testid="partner-company-input" {...register("company")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">{d.message}</Label>
            <Textarea className={inputClass} rows={3} data-testid="partner-message-input" {...register("message")} />
          </div>
          <button type="submit" disabled={sending} className={submitClass} data-testid="partner-submit-btn">
            {sending ? "…" : d.submit}
          </button>
          <p className="text-xs text-slate-500 leading-relaxed" data-testid="partner-gdpr">
            {t.common.gdprNotice}{" "}
            <Link to={pathFor("privacy", lang)} onClick={() => onOpenChange(false)} className="text-slate-400 underline underline-offset-2 hover:text-[#CEA26B] transition-colors">
              {t.privacy.title}
            </Link>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const FormDialogsProvider = ({ children }) => {
  const [ticketOpen, setTicketOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState("");
  const [partnerPrefill, setPartnerPrefill] = useState("");

  const openTicket = (prefill = "") => {
    setTicketPrefill(prefill);
    setTicketOpen(true);
  };
  const openPartner = (prefill = "") => {
    setPartnerPrefill(prefill);
    setPartnerOpen(true);
  };

  return (
    <FormDialogsContext.Provider value={{ openTicket, openPartner }}>
      {children}
      <TicketDialog open={ticketOpen} onOpenChange={setTicketOpen} prefill={ticketPrefill} />
      <PartnerDialog open={partnerOpen} onOpenChange={setPartnerOpen} prefill={partnerPrefill} />
    </FormDialogsContext.Provider>
  );
};
